export interface Guide {
  slug: string;
  title: string;
  tags: string[];
  body: string;
}

const GETTING_STARTED_BODY = `# Getting Started

LocalAgent runs your AI agents against the model providers you configure. Getting a
working setup takes three steps: configure a provider, pick (or add) a model, and
create a persona that uses it.

## 1. Configure a provider

Open **Agents → Providers**. Each provider row shows how many models it exposes and
whether credentials are configured. Cloud providers (Anthropic, OpenAI, Groq,
Fireworks, and others) need an API key: if your platform operator has not configured
one, an account admin can add an account-scoped key in the **Provider Key** section of
the provider's detail panel. Keys are validated against the live provider before they
are stored, and they are encrypted at rest. LM Studio is the local exception — it uses
your desktop device's running LM Studio instance and needs no key.

## 2. Pick or add a model

Every provider carries a model catalog with two tiers. **Global** entries are read-only
mirrors of the platform's seeded catalog — use **Sync Global** to refresh them. **Account**
entries belong to your account: create one with **+ Add Model**, copy a global entry to
customize it, or use **Discover…** to fetch the provider's live model list and import
models directly. An entry's *name* is how personas reference it; its *model id* is what
the provider actually serves — the two are independent, so you can rename without
breaking anything.

## 3. Create a persona

Open **Agents → Agents** and create a new agent (persona). Give it a name, a system
prompt, and select the provider + model entry it should use. Optionally attach an
agent process — the reasoning structure the persona runs each turn (see Key Concepts).
Seeded default personas ship with sensible flagship processes already attached, so the
fastest path is to start from one of those and edit it.

## 4. Chat

Head to **Chat**, pick your persona, and send a message. On desktop you can attach a
workspace so the agent can read and edit files; on the web, chats run workspace-less.
If a turn fails with a provider error, check the provider's key configuration and the
model entry's context-window setting under **Agents → Providers**.
`;

const KEY_CONCEPTS_BODY = `# Key Concepts

## Personas

A persona is a configured agent: a name, a system prompt, a provider + model entry,
and optional defaults such as an attached agent process. Personas are account-scoped —
read-only **Templates** come seeded with the platform, and anything you create or copy
lives in your account and is fully editable. When you start a chat you choose the
persona that drives it; the persona decides which model answers and how it behaves.

## Agent processes

An agent process is the reasoning structure a session runs each turn — a validated
graph of steps ending in a single respond terminal. The seeded catalog ranges from
**Simple** (one pass, answer directly) to goal-driven processes that plan, execute,
self-evaluate, and refine across multiple passes. Steps can name their own persona (a
researcher step, an auditor step) or inherit the session's persona. Every session
resolves a process when it is created: an explicit choice wins, then the persona's
default, then Simple. Copy a system process into your account to customize it.

A persona can instead enable **automatic process selection** and assign the processes
it permits. For each new message, a dedicated internal agent considers the current
conversation plus those processes' descriptions and steps, then records the selected
process and reason in the turn details. A chat can inherit the persona setting or hold
an explicit override. The configured manual process is retained while automatic mode
is on and returns unchanged when automatic mode is disabled.

Internal planning, evaluation, selection, and compaction agents use the same
process-controlled execution system. Each chat's **Internal agents** setting controls
how their progress is shown: **Friendly** uses product-facing role labels, while
**Exact** also shows the system persona and process provenance. This is a display
choice for that chat only and does not change how any agent runs.

## Workspaces

A workspace connects an agent to a filesystem — a repository the agent can read,
search, and edit with real tools. Workspaces are a desktop capability: the desktop app
registers local folders, and cloud workspaces provision an isolated pod around a cloud
repository. Web chats always run workspace-less. Cloud workspaces hibernate when idle
and resume on demand, and their \`.shogunchop/\` directory carries steering rules and
skills the agent reads fresh every turn.

## Context sources

Context sources feed extra knowledge into a session beyond the chat history —
documents, integration data, and other account content the agent can draw on. The
session's context store is the durable system of record: every message and tool result
is persisted server-side, and long conversations are compacted by summarization rather
than truncation, so agents keep working memory without losing the verbatim recent
window. Search across your account's indexed content is available to agents as a tool
and to you on the **Search** page.
`;

export const GUIDES: Guide[] = [
  {
    slug: 'getting-started',
    title: 'Getting Started',
    tags: ['setup', 'providers', 'models', 'personas'],
    body: GETTING_STARTED_BODY,
  },
  {
    slug: 'key-concepts',
    title: 'Key Concepts',
    tags: ['concepts', 'personas', 'agent-processes', 'workspaces', 'context'],
    body: KEY_CONCEPTS_BODY,
  },
];
